import React, { useState } from 'react'
import { useLanguage } from '../i18n/LanguageContext'
import MetaTags from '../components/MetaTags'

export default function ContactPage() {
  const { t } = useLanguage()
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
    website_url: '' // Honeypot field
  })

  const [status, setStatus] = useState('idle') // 'idle' | 'submitting' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('')

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setStatus('submitting')
    setErrorMessage('')

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          message: formData.message,
          website_url: formData.website_url // Mandatory honeypot field
        })
      })

      if (response.ok) {
        setStatus('success')
        setFormData({
          name: '',
          phone: '',
          email: '',
          message: '',
          website_url: ''
        })
      } else {
        const errData = await response.json().catch(() => ({}))
        setStatus('error')
        setErrorMessage(errData.message || t.contact.errorGeneric)
      }
    } catch (err) {
      setStatus('error')
      setErrorMessage(t.contact.errorConnection)
    }
  }

  return (
    <div className="bg-slate-50 min-h-screen pb-20">
      <MetaTags
        title={t.contact.metaTitle}
        description={t.contact.metaDesc}
      />

      {/* Header Banner */}
      <section className="bg-gradient-to-br from-slate-100 via-sky-50/60 to-white text-slate-900 py-12 lg:py-16 border-b border-slate-200 relative overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-2xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 mb-4">
              {t.contact.headerTitle}
            </h1>
            <p className="text-slate-600 text-base sm:text-lg leading-relaxed">
              {t.contact.headerSubtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Main Grid */}
      <section className="py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          
          {/* Contact Details Column */}
          <div className="lg:col-span-5 space-y-8">
            <div className="space-y-6">
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900 border-b border-slate-200 pb-4">
                {t.contact.companyInfoTitle}
              </h2>

              <div className="space-y-5 text-slate-700">
                <div className="flex items-start gap-3.5">
                  <div className="text-sky-600 shrink-0 w-5 flex justify-center text-lg pt-0.5">
                    <i className="fa-solid fa-building"></i>
                  </div>
                  <div>
                    <span className="font-bold text-slate-900 block text-base">M.Rou Transport</span>
                    <span className="text-slate-500 text-sm">CVR: 42145572</span>
                  </div>
                </div>

                <div className="flex items-start gap-3.5">
                  <div className="text-sky-600 shrink-0 w-5 flex justify-center text-lg pt-0.5">
                    <i className="fa-solid fa-location-dot"></i>
                  </div>
                  <div>
                    <span className="font-bold text-slate-900 block text-base">{t.contact.addressLabel}</span>
                    <span className="text-slate-600 text-sm">{t.contact.addressVal}</span>
                  </div>
                </div>

                <div className="flex items-start gap-3.5">
                  <div className="text-sky-600 shrink-0 w-5 flex justify-center text-lg pt-0.5">
                    <i className="fa-solid fa-phone"></i>
                  </div>
                  <div>
                    <span className="font-bold text-slate-900 block text-base">{t.contact.phoneLabel}</span>
                    <a 
                      href="tel:+4525303016" 
                      className="text-sky-600 hover:underline text-sm font-semibold whitespace-nowrap inline-block"
                    >
                      <span className="whitespace-nowrap">+45 25 30 30 16</span>
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-3.5">
                  <div className="text-sky-600 shrink-0 w-5 flex justify-center text-lg pt-0.5">
                    <i className="fa-solid fa-envelope"></i>
                  </div>
                  <div>
                    <span className="font-bold text-slate-900 block text-base">{t.contact.emailLabel}</span>
                    <a 
                      href="mailto:mr@mrou.dk" 
                      className="text-sky-600 hover:underline text-sm font-semibold"
                    >
                      mr@mrou.dk
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-3.5">
                  <div className="text-sky-600 shrink-0 w-5 flex justify-center text-lg pt-0.5">
                    <i className="fa-solid fa-clock"></i>
                  </div>
                  <div>
                    <span className="font-bold text-slate-900 block text-base">{t.contact.hoursLabel}</span>
                    <span className="text-slate-600 text-sm leading-relaxed block">
                      {t.contact.hoursVal}
                    </span>
                  </div>
                </div>

                <div className="flex items-start gap-3.5 pt-2">
                  <div className="text-sky-600 shrink-0 w-5 flex justify-center text-lg pt-0.5">
                    <i className="fa-solid fa-share-nodes"></i>
                  </div>
                  <div>
                    <span className="font-bold text-slate-900 block text-base mb-2">{t.contact.socialLabel}</span>
                    <div className="flex items-center gap-3">
                      <a 
                        href="https://www.facebook.com/M.RouTransport/" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        aria-label="Facebook"
                        className="w-10 h-10 rounded-full bg-slate-200 hover:bg-sky-600 hover:text-white text-slate-700 flex items-center justify-center transition-colors"
                      >
                        <i className="fa-brands fa-facebook-f text-base"></i>
                      </a>
                      <a 
                        href="https://www.instagram.com/m.rou_transport/" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        aria-label="Instagram"
                        className="w-10 h-10 rounded-full bg-slate-200 hover:bg-sky-600 hover:text-white text-slate-700 flex items-center justify-center transition-colors"
                      >
                        <i className="fa-brands fa-instagram text-base"></i>
                      </a>
                      <a 
                        href="https://www.tiktok.com/@mroutransport" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        aria-label="TikTok"
                        className="w-10 h-10 rounded-full bg-slate-200 hover:bg-sky-600 hover:text-white text-slate-700 flex items-center justify-center transition-colors"
                      >
                        <i className="fa-brands fa-tiktok text-base"></i>
                      </a>
                    </div>
                  </div>
                </div>

              </div>
            </div>

            {/* Visual Box */}
            <div className="p-6 bg-slate-900 text-white rounded-none shadow-sm space-y-3">
              <h3 className="font-bold text-lg">{t.home.ctaTitle}</h3>
              <p className="text-slate-300 text-xs leading-relaxed">
                {t.home.ctaSubtitle}
              </p>
              <div className="pt-2">
                <a
                  href="tel:+4525303016"
                  className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-600 text-white font-bold py-2 px-4 rounded-none text-xs transition-colors whitespace-nowrap"
                >
                  <i className="fa-solid fa-phone text-xs"></i>
                  <span className="whitespace-nowrap">+45 25 30 30 16</span>
                </a>
              </div>
            </div>
          </div>

          {/* Contact Webform Column */}
          <div className="lg:col-span-7">
            <div className="bg-white p-8 sm:p-10 rounded-none border border-slate-200 shadow-sm">
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2">
                {t.contact.formTitle}
              </h2>
              <p className="text-slate-600 text-sm mb-6">
                {t.contact.formSubtitle}
              </p>

              {status === 'success' ? (
                <div className="p-6 bg-emerald-50 border border-emerald-200 rounded-none text-center space-y-4">
                  <div className="w-14 h-14 bg-emerald-500 text-white rounded-full flex items-center justify-center mx-auto text-2xl shadow-sm">
                    <i className="fa-solid fa-check"></i>
                  </div>
                  <h3 className="text-xl font-bold text-emerald-900">{t.contact.successTitle}</h3>
                  <p className="text-emerald-700 text-sm leading-relaxed max-w-md mx-auto">
                    {t.contact.successDesc}
                  </p>
                  <button
                    onClick={() => setStatus('idle')}
                    className="mt-4 inline-block bg-slate-900 hover:bg-slate-800 text-white font-bold py-2.5 px-6 rounded-none text-xs transition-colors"
                  >
                    {t.contact.sendNewBtn}
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Mandatory SR-ONLY Absolute Honeypot Field */}
                  <div className="sr-only absolute pointer-events-none" aria-hidden="true">
                    <label htmlFor="website_url">Website URL</label>
                    <input
                      type="text"
                      id="website_url"
                      name="website_url"
                      tabIndex={-1}
                      autoComplete="off"
                      value={formData.website_url}
                      onChange={handleChange}
                    />
                  </div>

                  <div>
                    <label htmlFor="name" className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
                      {t.contact.nameLabel} <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      placeholder={t.contact.namePlaceholder}
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-none border border-slate-300 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 text-slate-900 text-sm outline-none transition-all"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="phone" className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
                        {t.contact.phoneFieldLabel} <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        required
                        placeholder={t.contact.phoneFieldPlaceholder}
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-none border border-slate-300 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 text-slate-900 text-sm outline-none transition-all"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
                        {t.contact.emailFieldLabel} <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        placeholder={t.contact.emailFieldPlaceholder}
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-none border border-slate-300 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 text-slate-900 text-sm outline-none transition-all"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
                      {t.contact.messageFieldLabel} <span className="text-rose-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={5}
                      placeholder={t.contact.messageFieldPlaceholder}
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-none border border-slate-300 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 text-slate-900 text-sm outline-none transition-all resize-none"
                    />
                  </div>

                  {status === 'error' && (
                    <div className="p-4 rounded-none bg-rose-50 border border-rose-200 text-rose-700 text-sm flex items-center gap-3">
                      <i className="fa-solid fa-circle-exclamation text-rose-500 text-lg shrink-0"></i>
                      <span>{errorMessage}</span>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'submitting'}
                    className="w-full bg-sky-500 hover:bg-sky-600 active:bg-sky-700 disabled:bg-slate-400 text-white font-bold text-sm py-3.5 px-6 rounded-none shadow hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 transition-all flex items-center justify-center gap-2 uppercase tracking-wider"
                  >
                    {status === 'submitting' ? (
                      <>
                        <i className="fa-solid fa-spinner animate-spin"></i>
                        <span>{t.contact.submittingBtn}</span>
                      </>
                    ) : (
                      <>
                        <span>{t.contact.submitBtn}</span>
                        <i className="fa-solid fa-paper-plane text-xs"></i>
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>

        </div>
      </section>
    </div>
  )
}
